import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import DataTable from './DataTable';
import { AdminPageLayout } from './AdminPageLayout';

export function RolesManagementTable({
  roles = [],
  onEditRole,
  onDeleteRole,
  canUpdateRole = true,
  canDeleteRole = true,
  loading = false,
  onSort,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 10,
  className = ''
}) {
  const safeRoles = Array.isArray(roles) ? roles : [];

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true
    },
    {
      key: 'permissions',
      label: 'Permissions',
      accessor: 'permissions',
      sortable: false,
      filterable: false,
      render: (permissions) => {
        if (!Array.isArray(permissions) || permissions.length === 0) {
          return (
            <span className="text-gray-400 italic text-sm">No permissions</span>
          );
        }

        // Group permissions by resource
        const groupedPermissions = permissions.reduce((acc, permission) => {
          const [resource] = permission.split(':');
          if (!acc[resource]) {
            acc[resource] = [];
          }
          acc[resource].push(permission);
          return acc;
        }, {});

        return (
          <div className="space-y-1.5">
            {Object.entries(groupedPermissions).map(([resource, perms]) => (
              <div key={resource} className="flex items-start gap-2">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider min-w-[50px] pt-0.5">
                  {resource.replace('_', ' ')}
                </span>
                <div className="flex flex-wrap gap-1">
                  {perms.map((permission) => {
                    const [, action] = permission.split(':');
                    const actionFormatted = action
                      .split('_')
                      .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                      .join(' ');

                    return (
                      <span
                        key={permission}
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/10"
                      >
                        {actionFormatted}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created',
      accessor: 'createdAt',
      sortable: true,
      filterable: true,
      type: 'date',
      filterType: 'date',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      filterable: false,
      render: (_, role) => (
        <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
          {canUpdateRole && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditRole?.(role);
              }}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
              title="Edit role permissions"
              type="button"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
          {canDeleteRole && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRole?.(role._id);
              }}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete role (cannot be undone)"
              type="button"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <AdminPageLayout className={className}>
      <DataTable
        data={safeRoles}
        columns={columns}
        loading={loading}
        onSort={onSort}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        searchable={true}
        filterable={true}
        sortable={true}
        selectable={false}
        emptyMessage="No roles found"
        showPagination={true}
        showSearch={true}
        showFilters={true}
        customActions={null}
      />
    </AdminPageLayout>
  );
}
